/**
 * Token 管理模块
 *
 * 职责：
 * 1. 集中化 token 读取（共享 Cookie → 内存 → localStorage fallback）
 * 2. 跨标签页同步（storage 事件监听）
 * 3. 不同过期场景的差异化文案 + 日志
 * 4. 上报到 Aegis 监控平台
 */
import { globalStore } from "@xmiles/holopix-config";
import { aegis } from "@/utils/monitoring";
import { getCookieByName, getCookieConfig } from "./authCookie";

/** 解码 JWT payload 获取用户身份信息 */
function decodeJwtPayload(token: string): { account?: string } | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return typeof payload === "object" ? payload : null;
  } catch {
    return null;
  }
}


/* ==================== 枚举 & 映射 ==================== */

/** Token 失效/登出场景枚举 */
export enum TokenExpiryReason {
  NORMAL_EXPIRY = "normal_expiry",         // code 90000，正常过期 / 后端踢下线
  CONCURRENT_LOGIN = "concurrent_login",   // code 90001，其它设备挤占
  STORAGE_CLEARED = "storage_cleared",     // localStorage 中丢失（另一标签页登出导致）
  API_401 = "api_401",                     // HTTP 401 响应
  TOKEN_PARSE_ERROR = "token_parse_error", // token 格式解析异常
  MANUAL_LOGOUT = "manual_logout",         // 用户主动登出
}

/** 每种场景对应的用户可见文案（保持简练，不过度打扰） */
export const TOKEN_ERROR_MESSAGES: Record<TokenExpiryReason, string> = {
  [TokenExpiryReason.NORMAL_EXPIRY]: "登录已过期，请重新登录",
  [TokenExpiryReason.CONCURRENT_LOGIN]: "您的账号已在其他设备登录",
  [TokenExpiryReason.STORAGE_CLEARED]: "登录信息已变更，请重新登录",
  [TokenExpiryReason.API_401]: "认证失败，请重新登录",
  [TokenExpiryReason.TOKEN_PARSE_ERROR]: "登录信息异常，请重新登录",
  [TokenExpiryReason.MANUAL_LOGOUT]: "", // 主动登出不弹 toast
};

/** 日志标签（用于 Aegis / console 快速定位） */
export const TOKEN_LOG_TAGS: Record<TokenExpiryReason, string> = {
  [TokenExpiryReason.NORMAL_EXPIRY]: "[TOKEN][EXP] 正常过期-90000",
  [TokenExpiryReason.CONCURRENT_LOGIN]: "[TOKEN][EXP] 其它设备登录-90001",
  [TokenExpiryReason.STORAGE_CLEARED]: "[TOKEN][EXP] 跨标签页-存储被清",
  [TokenExpiryReason.API_401]: "[TOKEN][EXP] HTTP-401-认证失败",
  [TokenExpiryReason.TOKEN_PARSE_ERROR]: "[TOKEN][ERR] Token格式异常",
  [TokenExpiryReason.MANUAL_LOGOUT]: "[TOKEN][LOG] 用户主动登出",
};

/* ==================== 常量 ==================== */

/** Pinia persist 的 localStorage key（对应 user store persist.key） */
const PINO_USER_STORE_KEY = "pinoUserStore";

/** 兼容旧版：有的页面可能直接写 'token' key */
const LEGACY_TOKEN_KEY = "token";

/* ==================== 内部标记位 ==================== */

/** 标记位：当前标签页的 token 是否因跨标签页同步而被清除（非主动登出） */
let crossTabTokenCleared = false;

/** 标记位：另一标签页登录了新账号，token 已变更，切回时需刷新页面同步身份 */
let crossTabUserUpdated = false;

/**
 * 判断当前标签页的 token 是否因跨标签页同步被清除
 * 供响应拦截器判断 90001 是否为被动触发
 */
export function isCrossTabTokenCleared(): boolean {
  return crossTabTokenCleared;
}

/* ==================== 内部工具 ==================== */

interface PinoUserStoreData {
  userInfo?: { token?: string; [k: string]: unknown };
  [k: string]: unknown;
}

/** 从 Pinia persist 的 localStorage 数据中提取 token */
function extractTokenFromPiniaStore(): string {
  try {
    const raw = localStorage.getItem(PINO_USER_STORE_KEY);
    if (!raw) return "";
    const parsed: PinoUserStoreData = JSON.parse(raw);
    return parsed?.userInfo?.token || "";
  } catch {
    return "";
  }
}

/** 将 pinia 持久化中的 token 写入 globalStore */
function syncPiniaTokenToMemory(): string {
  const lsToken = extractTokenFromPiniaStore();
  if (lsToken) {
    const memToken = globalStore.get("token") as string | undefined;
    if (lsToken !== memToken) {
      globalStore.set("token", lsToken);
    }
  }
  return lsToken;
}

/* ==================== 公开 API ==================== */

/**
 * 获取当前有效 token
 * 优先级：共享 Cookie > globalStore（内存） > Pinia persist（localStorage）
 * 后两者为历史 PC 登录态和跨标签页场景提供 fallback
 */
export function getToken(): string {
  const { name } = getCookieConfig();
  const cookieToken = getCookieByName(name);
  if (cookieToken) {
    const memToken = globalStore.get("token") as string | undefined;
    if (memToken !== cookieToken) {
      globalStore.set("token", cookieToken);
    }
    crossTabTokenCleared = false;
    return cookieToken;
  }

  const memToken = globalStore.get("token") as string | undefined;
  if (memToken) {
    // 成功获取到有效 token，重置跨标签页清除标记
    crossTabTokenCleared = false;
    return memToken;
  }
  const lsToken = syncPiniaTokenToMemory();
  if (lsToken) {
    crossTabTokenCleared = false;
  }
  return lsToken;
}

/**
 * 获取 token 并记录来源（用于调试）
 */
export function getTokenWithSource(): { token: string; source: "cookie" | "memory" | "localStorage" | "none" } {
  const { name } = getCookieConfig();
  const cookieToken = getCookieByName(name);
  if (cookieToken) return { token: cookieToken, source: "cookie" };
  const memToken = globalStore.get("token") as string | undefined;
  if (memToken) return { token: memToken, source: "memory" };
  const lsToken = extractTokenFromPiniaStore();
  if (lsToken) return { token: lsToken, source: "localStorage" };
  return { token: "", source: "none" };
}

/**
 * 被动失效（90000/90001/401）时先比对共享 Cookie 凭证：
 * Cookie 里已有另一个不同的 token，说明其他标签页/端刚写入新登录态，本页只是持有旧 token，
 * 此时应静默换新而不是登出——否则被动清理会清掉共享存储/Cookie 并广播 auth-reset，
 * 把刚登录成功的标签页一起踢下线（多标签页连环互踢的根源）。
 *
 * @param failedToken 失败请求实际携带的 token（从请求 config.headers.token 取）。
 *                    必须优先于内存 token 比对：跨标签页竞态下 storage 同步可能已把内存
 *                    更新成新 token，用内存比对会误判"一致"而错过换新时机。
 * @returns "adopted" 已换新为共享凭证，调用方应刷新本页同步身份；
 *          "unchanged" 共享凭证与失败请求一致（确实是本页持有的失效 token），调用方按原逻辑登出清理
 */
export function tryAdoptSharedCredential(failedToken?: string): "adopted" | "unchanged" {
  const { name } = getCookieConfig();
  const cookieToken = getCookieByName(name);
  const staleToken = failedToken || (globalStore.get("token") as string | undefined) || extractTokenFromPiniaStore();
  if (cookieToken && staleToken && cookieToken !== staleToken) {
    globalStore.set("token", cookieToken);
    crossTabTokenCleared = false;
    crossTabUserUpdated = false;
    return "adopted";
  }
  return "unchanged";
}

/** 从 axios request config 中取出业务请求携带的 token（请求拦截器写入 config.headers.token） */
export function extractTokenFromRequestConfig(config?: { headers?: unknown }): string {
  const headers = config?.headers as Record<string, unknown> | undefined;
  if (!headers) return "";
  const token = headers["token"] ?? headers["Token"];
  return typeof token === "string" ? token : "";
}

/**
 * 判断当前是否有有效 token
 */
export function hasToken(): boolean {
  return !!getToken();
}

/**
 * 记录 token 过期/变更日志到 Aegis + console
 */
export function logTokenExpiry(reason: TokenExpiryReason, extra?: Record<string, unknown>) {
  const tag = TOKEN_LOG_TAGS[reason];
  const msg = TOKEN_ERROR_MESSAGES[reason];
  const logData = {
    reason,
    tag,
    userMsg: msg,
    timestamp: Date.now(),
    url: window.location.href,
    ...extra,
  };

  console.warn(tag, reason, logData);

  // 上报到 Aegis
  if (aegis) {
    try {
      if (typeof aegis.report === "function") {
        aegis.report({ msg: tag, ...logData });
      } else if (typeof aegis.error === "function") {
        aegis.error(tag + " " + JSON.stringify(logData));
      }
    } catch {
      // 静默
    }
  }
}

/**
 * 记录 token 相关的一般日志（非过期场景）
 */
export function logToken(msg: string, data?: Record<string, unknown>) {
  console.log("[TOKEN]", msg, data);
  if (aegis) {
    try {
      if (typeof aegis.report === "function") {
        aegis.report({ msg: "[TOKEN] " + msg, ...data });
      }
    } catch {
      // 静默
    }
  }
}

/* ==================== 跨标签页同步 ==================== */

let storageListenerInitialized = false;
let crossTabRefreshListenerInitialized = false;

/**
 * 注册切回标签页时的自动刷新监听（仅注册一次）
 * 当用户在另一标签页登录新账号后，切回本标签页时自动刷新同步身份
 */
function initCrossTabRefreshOnReturn() {
  if (crossTabRefreshListenerInitialized) return;
  crossTabRefreshListenerInitialized = true;

  const doReloadIfNeeded = () => {
    if (crossTabUserUpdated && document.visibilityState === "visible") {
      logToken("跨标签页用户变更，切回时自动刷新页面", {
        currentUrl: window.location.href,
      });
      window.location.reload();
    }
  };

  document.addEventListener("visibilitychange", () => {
    doReloadIfNeeded();
  });

  window.addEventListener("focus", () => {
    doReloadIfNeeded();
  });

  logToken("跨标签页切回刷新监听已注册");
}

/**
 * 初始化跨标签页 token 同步监听
 * 当其他标签页登出/登录时，当前标签页自动同步
 */
export function initTokenStorageListener() {
  if (storageListenerInitialized) return;
  storageListenerInitialized = true;

  window.addEventListener("storage", (event: StorageEvent) => {
    // 1) Pinia persist key 变化
    if (event.key === PINO_USER_STORE_KEY) {
      const memToken = globalStore.get("token") as string | undefined;

      if (!event.newValue) {
        // 另一个标签页清除了 localStorage（登出）
        if (memToken) {
          crossTabTokenCleared = true;
          logTokenExpiry(TokenExpiryReason.STORAGE_CLEARED, {
            oldTokenPrefix: memToken.substring(0, 8),
            source: "pinoUserStore_deleted",
            currentUrl: window.location.href,
          });
          globalStore.set("token", undefined);
        }
      } else {
        // 另一个标签页更新了用户信息（登录/登出）
        try {
          const parsed: PinoUserStoreData = JSON.parse(event.newValue);
          const newToken = parsed?.userInfo?.token;
          if (newToken && newToken !== memToken) {
            // 解码 JWT payload 比较用户身份，而不是比较 token 字符串
            const newPayload = decodeJwtPayload(newToken);
            const oldPayload = memToken ? decodeJwtPayload(memToken) : null;

            const sameUser =
              newPayload?.account && oldPayload?.account && newPayload.account === oldPayload.account;

            if (sameUser) {
              // 同一用户的 token refresh（如新标签页 queryUserInfo 拉到了新 token）
              // 更新内存 token 但不触发跨标签页刷新
              globalStore.set("token", newToken);
              crossTabUserUpdated = false;
              logToken("跨标签页token刷新（同一用户）", {
                account: newPayload.account,
                newTokenPrefix: newToken.substring(0, 8),
                currentUrl: window.location.href,
              });
              return;
            }

            // 真正的不同用户登录
            globalStore.set("token", newToken);
            crossTabUserUpdated = true;
            logToken("跨标签页检测到新登录用户", {
              newTokenPrefix: newToken.substring(0, 8),
              oldTokenPrefix: (memToken || "").substring(0, 8),
              currentUrl: window.location.href,
            });
            initCrossTabRefreshOnReturn();
          } else if (!newToken && memToken) {
            // 另一标签页登出，token 为空
            crossTabTokenCleared = true;
            logTokenExpiry(TokenExpiryReason.STORAGE_CLEARED, {
              oldTokenPrefix: memToken.substring(0, 8),
              source: "pinoUserStore_token_cleared",
              currentUrl: window.location.href,
            });
            globalStore.set("token", undefined);
          }
        } catch {
          // JSON parse 失败，静默
        }
      }
    }

    // 2) 旧版 localStorage 'token' key
    // 有些老代码或第三方脚本可能直接用这个 key
    if (event.key === LEGACY_TOKEN_KEY) {
      if (!event.newValue) {
        // 'token' 被删除
        const memToken = globalStore.get("token") as string | undefined;
        if (memToken && !extractTokenFromPiniaStore()) {
          crossTabTokenCleared = true;
          logTokenExpiry(TokenExpiryReason.STORAGE_CLEARED, {
            oldTokenPrefix: memToken.substring(0, 8),
            source: "legacy_token_key",
            currentUrl: window.location.href,
          });
          globalStore.set("token", undefined);
        }
      }
    }
  });

  logToken("跨标签页 storage 监听已初始化");
}

/**
 * 初始化整个 Token 管理模块（启动时调用一次即可）
 */
export function initTokenManager() {
  // 初始化 storage 监听
  initTokenStorageListener();

  // 从 localStorage 恢复 token（如果内存中没有）
  const token = getToken();
  if (token) {
    logToken("Token管理器初始化完成", { tokenPrefix: token.substring(0, 8) });
  } else {
    logToken("Token管理器初始化完成（无 token）");
  }
}

export default {
  getToken,
  hasToken,
  getTokenWithSource,
  tryAdoptSharedCredential,
  extractTokenFromRequestConfig,
  isCrossTabTokenCleared,
  logTokenExpiry,
  logToken,
  initTokenManager,
  initTokenStorageListener,
  TokenExpiryReason,
};
